import { useEffect, useState } from 'react';
import { api, type Category } from '../lib/api';
import { ErrorNote, Money, Sheet, Spinner } from '../components/ui';

interface PreviewDay {
  entryDate: string;
  dayType: 'full' | 'first_day' | 'last_day' | 'single_day';
  prorationPercent: string;
  breakfastClaimed: boolean;
  lunchClaimed: boolean;
  dinnerClaimed: boolean;
  incidentalsClaimed: boolean;
  amount: string;
}

interface Preview {
  rateId: string;
  rateName: string;
  days: PreviewDay[];
  components: { breakfastAmount: string; lunchAmount: string; dinnerAmount: string; incidentalsAmount: string };
  total: string;
}

/**
 * One card per travel day with three meal toggles.
 *
 * The copy matters: people do not know what "M&IE proration" means, but they
 * do know whether the hotel gave them breakfast. Unchecking a meal deducts it
 * at full value before the day's percentage is applied -- see
 * packages/shared/per-diem.ts for why that order is the whole ballgame.
 */
export function PerDiemSheet({
  open, onClose, reportId, categories, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  categories: Category[];
  onSaved: () => void | Promise<void>;
}) {
  const perDiemCategory = categories.find((c) => c.appliesToKind === 'per_diem');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [days, setDays] = useState<PreviewDay[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setError(null);
    api.post<Preview>(`/reports/${reportId}/per-diem/preview`)
      .then((p) => { setPreview(p); setDays(p.days); })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not build the per-diem preview'));
  }, [open, reportId]);

  function toggleMeal(index: number, meal: 'breakfastClaimed' | 'lunchClaimed' | 'dinnerClaimed') {
    setDays((prev) => prev.map((d, i) => (i === index ? { ...d, [meal]: !d[meal] } : d)));
  }

  function setAllMeals(meal: 'breakfastClaimed' | 'lunchClaimed' | 'dinnerClaimed', claimed: boolean) {
    setDays((prev) => prev.map((d) => ({ ...d, [meal]: claimed })));
  }

  /** Mirrors the server formula so the card total updates as meals are toggled. */
  function localAmount(day: PreviewDay): number {
    if (!preview) return 0;
    const c = preview.components;
    const subtotal =
      (day.breakfastClaimed ? Number(c.breakfastAmount) : 0) +
      (day.lunchClaimed ? Number(c.lunchAmount) : 0) +
      (day.dinnerClaimed ? Number(c.dinnerAmount) : 0) +
      (day.incidentalsClaimed ? Number(c.incidentalsAmount) : 0);
    return Math.round(subtotal * Number(day.prorationPercent) * 100) / 100;
  }

  const total = days.reduce((sum, d) => sum + localAmount(d), 0);

  async function save() {
    if (!perDiemCategory || !preview) { setError('No per-diem category is configured'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.post(`/reports/${reportId}/per-diem`, {
        rateId: preview.rateId,
        categoryId: perDiemCategory.id,
        days: days.map((d) => ({
          entryDate: d.entryDate,
          dayType: d.dayType,
          breakfastClaimed: d.breakfastClaimed,
          lunchClaimed: d.lunchClaimed,
          dinnerClaimed: d.dinnerClaimed,
          incidentalsClaimed: d.incidentalsClaimed,
        })),
      });
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the per diem');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Per diem"
      footer={
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-600">Total</span>
            <Money value={total} className="text-lg font-semibold" />
          </div>
          <button type="button" className="btn-primary w-full" disabled={busy || !preview}
                  onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save per diem'}
          </button>
        </div>
      }
    >
      {!preview && !error ? <Spinner label="Working out the days" /> : null}
      <ErrorNote>{error}</ErrorNote>

      {preview ? (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Uncheck any meal your hotel or the conference provided — it is deducted in full.
          </p>
          <p className="text-xs text-slate-500">Rate: {preview.rateName}</p>

          <div className="flex flex-wrap gap-2 rounded-lg bg-slate-50 p-2 text-xs">
            <span className="self-center text-slate-500">Uncheck for every day:</span>
            {(['breakfastClaimed', 'lunchClaimed', 'dinnerClaimed'] as const).map((meal) => (
              <button key={meal} type="button" onClick={() => setAllMeals(meal, false)}
                      className="rounded border border-slate-300 bg-white px-2 py-1">
                {meal.replace('Claimed', '')}
              </button>
            ))}
          </div>

          {days.map((day, i) => (
            <div key={day.entryDate} className="card p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{day.entryDate}</p>
                  <span className="text-xs text-slate-500">
                    {day.dayType.replace('_', ' ')} · {Math.round(Number(day.prorationPercent) * 100)}%
                  </span>
                </div>
                <Money value={localAmount(day)} className="font-semibold" />
              </div>
              <div className="mt-2 flex gap-2">
                {([['breakfastClaimed', 'B'], ['lunchClaimed', 'L'], ['dinnerClaimed', 'D']] as const).map(
                  ([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={day[key]}
                      onClick={() => toggleMeal(i, key)}
                      className={`h-11 flex-1 rounded-lg border text-sm font-medium ${
                        day[key]
                          ? 'border-brand bg-brand-light text-brand'
                          : 'border-slate-300 bg-white text-slate-400 line-through'
                      }`}
                    >
                      {label}
                    </button>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </Sheet>
  );
}
